import { module as BaseModule } from "modujs";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import ImageGalleryOverlay from "../components/ImageGalleryOverlay.jsx";

const SELECTORS = {
  viewport: "[data-image-gallery-viewport]",
  track: "[data-image-gallery-track]",
  item: "[data-image-gallery-item]",
  trigger: "[data-image-gallery-trigger]",
  modalSource: "[data-image-gallery-modal-source]",
  caption: ".b-image-gallery__item-caption",
  previous: "[data-image-gallery-previous]",
  next: "[data-image-gallery-next]",
  pagination: "[data-image-gallery-pagination]",
  dot: "[data-image-gallery-dot]",
  heading: ".c-section-heading__heading",
  eyebrow: ".c-section-heading__eyebrow",
};

export default class ImageGallery extends BaseModule {
  init() {
    this.viewport = this.el.querySelector(SELECTORS.viewport);
    this.track = this.el.querySelector(SELECTORS.track);
    this.itemEls = Array.from(this.el.querySelectorAll(SELECTORS.item));
    this.previousButton = this.el.querySelector(SELECTORS.previous);
    this.nextButton = this.el.querySelector(SELECTORS.next);
    this.pagination = this.el.querySelector(SELECTORS.pagination);
    this.dots = Array.from(this.el.querySelectorAll(SELECTORS.dot));
    this.currentIndex = 0;
    this.positionCount = 1;
    this.pointerStartX = null;
    this.pointerDeltaX = 0;
    this.isDragging = false;
    this.preventTriggerClick = false;

    if (!this.viewport || !this.track || !this.itemEls.length) {
      return;
    }

    this.items = this.collectItems();
    this.heading =
      this.el.querySelector(SELECTORS.heading)?.textContent.trim() ||
      "Image gallery";
    this.eyebrow =
      this.el.querySelector(SELECTORS.eyebrow)?.textContent.trim() || "";
    this.slimModal = this.el.dataset.imageGallerySlimModal === "true";

    this.mountTarget = document.createElement("div");
    this.mountTarget.className = "b-image-gallery__overlay-root";
    this.el.appendChild(this.mountTarget);

    this.isOpen = false;
    this.modalIndex = 0;

    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClose = this.handleClose.bind(this);

    this.attachListeners();
    this.updateLayout();
    this.renderOverlay();
  }

  collectItems() {
    return this.itemEls.map((item, index) => ({
      id: `image-gallery-item-${index}`,
      imageHTML:
        item.querySelector(SELECTORS.modalSource)?.innerHTML.trim() || "",
      caption:
        item.querySelector(SELECTORS.caption)?.textContent.trim() || "",
    }));
  }

  getItemsPerView() {
    const value = Number.parseInt(
      window.getComputedStyle(this.viewport).getPropertyValue(
        "--image-gallery-items-per-view"
      ),
      10
    );

    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  getMaxIndex() {
    return Math.max(0, this.itemEls.length - this.getItemsPerView());
  }

  getStep() {
    if (!this.itemEls.length) {
      return 0;
    }

    const itemWidth = this.itemEls[0].getBoundingClientRect().width;
    const trackStyles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(
      trackStyles.columnGap || trackStyles.gap || "0"
    );

    return itemWidth + gap;
  }

  goToPosition(index) {
    this.currentIndex = Math.max(0, Math.min(this.getMaxIndex(), index));
    this.updatePosition();
  }

  updateLayout() {
    const maxIndex = this.getMaxIndex();
    this.positionCount = maxIndex + 1;
    this.currentIndex = Math.min(this.currentIndex, maxIndex);
    this.updatePosition();
  }

  updatePosition() {
    const offset = this.currentIndex * this.getStep();
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;

    const atStart = this.currentIndex === 0;
    const atEnd = this.currentIndex === this.getMaxIndex();

    if (this.previousButton) {
      this.previousButton.disabled = atStart;
      this.previousButton.setAttribute("aria-disabled", String(atStart));
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.setAttribute("aria-disabled", String(atEnd));
    }

    const hasMultiplePositions = this.positionCount > 1;
    if (this.previousButton) this.previousButton.hidden = !hasMultiplePositions;
    if (this.nextButton) this.nextButton.hidden = !hasMultiplePositions;
    if (this.pagination) this.pagination.hidden = !hasMultiplePositions;

    this.dots.forEach((dot, index) => {
      const isAvailable = index < this.positionCount;
      dot.hidden = !isAvailable;
      dot.setAttribute(
        "aria-current",
        isAvailable && index === this.currentIndex ? "true" : "false"
      );
    });
  }

  handlePrevious() {
    this.goToPosition(this.currentIndex - 1);
  }

  handleNext() {
    this.goToPosition(this.currentIndex + 1);
  }

  handleKeyDown(event) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.handlePrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.handleNext();
    }
  }

  handleResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.updateLayout(), 150);
  }

  getClientX(event) {
    return event.touches?.[0]?.clientX ?? event.changedTouches?.[0]?.clientX ?? event.clientX;
  }

  handlePointerDown(event) {
    if (event.type === "mousedown" && event.button !== 0) {
      return;
    }

    this.pointerStartX = this.getClientX(event);
    this.pointerDeltaX = 0;
    this.isDragging = true;
    this.track.classList.add("is-dragging");
  }

  handlePointerMove(event) {
    if (!this.isDragging || this.pointerStartX === null) {
      return;
    }

    const clientX = this.getClientX(event);
    this.pointerDeltaX = clientX - this.pointerStartX;

    if (Math.abs(this.pointerDeltaX) > 6) {
      this.preventTriggerClick = true;
    }

    const baseOffset = this.currentIndex * this.getStep();
    this.track.style.transform = `translate3d(${
      -baseOffset + this.pointerDeltaX
    }px, 0, 0)`;
  }

  handlePointerUp() {
    if (!this.isDragging) {
      return;
    }

    const threshold = Math.min(80, this.getStep() * 0.25);

    if (this.pointerDeltaX <= -threshold) {
      this.goToPosition(this.currentIndex + 1);
    } else if (this.pointerDeltaX >= threshold) {
      this.goToPosition(this.currentIndex - 1);
    } else {
      this.updatePosition();
    }

    this.isDragging = false;
    this.pointerStartX = null;
    this.pointerDeltaX = 0;
    this.track.classList.remove("is-dragging");

    window.setTimeout(() => {
      this.preventTriggerClick = false;
    }, 0);
  }

  openModal(index) {
    this.modalIndex = index;
    this.isOpen = true;
    this.el.classList.add("has-open-modal");
    this.renderOverlay();
  }

  handleClose() {
    this.isOpen = false;
    this.el.classList.remove("has-open-modal");
    this.renderOverlay();
  }

  attachListeners() {
    this.previousButton?.addEventListener("click", this.handlePrevious);
    this.nextButton?.addEventListener("click", this.handleNext);
    this.viewport.addEventListener("keydown", this.handleKeyDown);
    this.viewport.addEventListener("mousedown", this.handlePointerDown);
    this.viewport.addEventListener("touchstart", this.handlePointerDown, {
      passive: true,
    });
    window.addEventListener("mousemove", this.handlePointerMove);
    window.addEventListener("mouseup", this.handlePointerUp);
    window.addEventListener("touchmove", this.handlePointerMove, {
      passive: true,
    });
    window.addEventListener("touchend", this.handlePointerUp);
    window.addEventListener("resize", this.handleResize);

    this.dots.forEach((dot, index) => {
      const handler = () => this.goToPosition(index);
      dot.addEventListener("click", handler);
      dot._imageGalleryHandler = handler;
    });

    this.itemEls.forEach((item, index) => {
      const trigger = item.querySelector(SELECTORS.trigger);
      if (!trigger) return;

      const handler = (event) => {
        if (this.preventTriggerClick) {
          event.preventDefault();
          return;
        }

        this.openModal(index);
      };

      trigger.addEventListener("click", handler);
      trigger._imageGalleryHandler = handler;
    });
  }

  detachListeners() {
    this.previousButton?.removeEventListener("click", this.handlePrevious);
    this.nextButton?.removeEventListener("click", this.handleNext);
    this.viewport?.removeEventListener("keydown", this.handleKeyDown);
    this.viewport?.removeEventListener("mousedown", this.handlePointerDown);
    this.viewport?.removeEventListener("touchstart", this.handlePointerDown);
    window.removeEventListener("mousemove", this.handlePointerMove);
    window.removeEventListener("mouseup", this.handlePointerUp);
    window.removeEventListener("touchmove", this.handlePointerMove);
    window.removeEventListener("touchend", this.handlePointerUp);
    window.removeEventListener("resize", this.handleResize);
    window.clearTimeout(this.resizeTimer);

    this.dots.forEach((dot) => {
      if (dot._imageGalleryHandler) {
        dot.removeEventListener("click", dot._imageGalleryHandler);
        delete dot._imageGalleryHandler;
      }
    });

    this.itemEls.forEach((item) => {
      const trigger = item.querySelector(SELECTORS.trigger);
      if (trigger?._imageGalleryHandler) {
        trigger.removeEventListener("click", trigger._imageGalleryHandler);
        delete trigger._imageGalleryHandler;
      }
    });
  }

  renderOverlay() {
    if (!this.mountTarget) {
      return;
    }

    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.mountTarget);
    }

    this.reactRoot.render(
      createElement(ImageGalleryOverlay, {
        isOpen: this.isOpen,
        items: this.items,
        initialIndex: this.modalIndex,
        heading: this.heading,
        eyebrow: this.eyebrow,
        slimModal: this.slimModal,
        onClose: this.handleClose,
      })
    );
  }

  destroy() {
    this.detachListeners();
    this.el.classList.remove("has-open-modal");

    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    this.mountTarget?.remove();
    this.track.style.transform = "";
    this.items = [];
    this.itemEls = [];
  }
}
